import { createContext, type ComponentChildren } from "preact"
import { useCallback, useContext, useRef, useState } from "preact/hooks"

export type ToastVariant = "error" | "success"

export interface Toast {
	id: number
	message: string
	variant: ToastVariant
}

interface ToastContextValue {
	toast: (message: string, variant?: ToastVariant) => void
}

const ToastContext = createContext<ToastContextValue>({
	toast: () => {},
})

export function useToast(): ToastContextValue {
	return useContext(ToastContext)
}

let nextId = 0

export function ToastProvider({ children }: { children: ComponentChildren }) {
	const [toasts, setToasts] = useState<Toast[]>([])
	const timeoutRef = useRef<Map<number, number>>(new Map())

	const dismiss = useCallback((id: number) => {
		const t = timeoutRef.current.get(id)
		if (t) {
			clearTimeout(t)
			timeoutRef.current.delete(id)
		}
		setToasts((prev) => prev.filter((t) => t.id !== id))
	}, [])

	const toast = useCallback(
		(message: string, variant: ToastVariant = "error") => {
			const id = nextId++
			setToasts((prev) => [...prev, { id, message, variant }])
			const t = window.setTimeout(() => dismiss(id), 6_000)
			timeoutRef.current.set(id, t)
		},
		[dismiss],
	)

	return (
		<ToastContext.Provider value={{ toast }}>
			{children}
			{toasts.length > 0 && (
				<div
					class="toast-container position-fixed bottom-0 end-0 p-3"
					style={{ zIndex: 10_000 }}
				>
					{toasts.map((t) => (
						<div
							key={t.id}
							class={`toast show ${t.variant === "success" ? "border-success" : "border-danger"}`}
							role="alert"
						>
							<div
								class={`toast-header ${t.variant === "success" ? "text-bg-success" : "text-bg-danger"}`}
							>
								<strong class="me-auto">
									{t.variant === "success" ? "Success" : "Error"}
								</strong>
								<button
									type="button"
									class="btn-close btn-close-white"
									aria-label="Close"
									onClick={() => dismiss(t.id)}
								/>
							</div>
							<div class="toast-body">{t.message}</div>
						</div>
					))}
				</div>
			)}
		</ToastContext.Provider>
	)
}
